// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Salient — bootstrap and the screen machine. Owns which screen is up, the room's Net (created ONCE
// per session and never left for a rematch), the canvas, and the setInterval that drives every
// clock. The rules live in sim.ts, the opponent in bot.ts, one match in battle.ts, the board in
// render.ts, the thumb in control.ts and the six scripted steps in tutorial.ts.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type RoundInfo, type RoundPlayer, type Rounds } from '@ben-gy/game-engine/rematch';
import { clearRoomInUrl, createLobby, createRoomEntry, normalizeRoomCode, setRoomInUrl } from '@ben-gy/game-engine/lobby';
import { resolveName } from '@ben-gy/game-engine/identity';
import { newSeed } from '@ben-gy/game-engine/rng';

import { Battle } from './battle';
import { DEFAULT_TIER, TIER_IDS, tierOf } from './bot';
import { buildState, clusterOf, DOUBLE_MS, DOUBLE_PX, DRAG_SLOP, orderFor, PRESS_MS, sameTypeAs, spread, unitAt, unitsIn, type Order } from './control';
import { runCountdown } from './countdown';
import { escapeHtml } from './dom';
import { MODE_IDS, modeOf } from './modes';
import { SEAT_NAME } from './palette';
import { boardSize, paint, project, tileRect, tonnageSplit, unproject, type View } from './render';
import { COLS } from './map';
import { Cmd, Stance, tileOf, tonnage, type SimEvent, type State } from './sim';
import { STEPS, allowed, setup as tutorialSetup } from './tutorial';
import { ATTRIB, aboutHtml, helpHtml, menuHtml, resultsHtml, waitingHtml } from './ui';
import { UNITS } from './units';
import { CHANNELS, type Wire } from './wire';
import { PATCHES } from './cues';

const SLUG = 'salient';
const MAX_SEATS = 2;

const app = document.querySelector<HTMLElement>('#app')!;
let content: HTMLElement;

const store = createStore(SLUG);
// The custom patches MUST be passed here: play() on an unknown cue is a silent no-op, so a missing
// patch is an inaudible bug rather than an error. See cues.ts.
const sfx = createSfx({ muted: store.get<boolean>('muted', false) ?? false, patches: PATCHES });
const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

let unlocked = false;
function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  try {
    sfx.unlock();
  } catch {
    /* audio unlock is best-effort */
  }
}
window.addEventListener('pointerdown', unlock, { once: true });
window.addEventListener('keydown', unlock, { once: true });

const playerName = resolveName(store, () => `Marshal ${Math.floor(Math.random() * 900 + 100)}`);
let myModeChoice = modeOf(store.get<string>('mode', '')).id;
let hostOptsMode = myModeChoice;
let myTier = tierOf(store.get<string>('tier', DEFAULT_TIER)).id;

const bestKey = (id: string): string => `best_${id}`;
const bestFor = (id: string): number => store.get<number>(bestKey(id), 0) ?? 0;

let net: Net | null = null;
let rounds: Rounds | null = null;
let battle: Battle | null = null;
let roster: RoundPlayer[] = [];
let tally: number[] = [0, 0];
let roomCode = '';
let roundNo = 0;
let soloRound = 0;

// ── screen lifecycle ────────────────────────────────────────────────────────────
const cleanups: Array<() => void> = [];
const onCleanup = (fn: () => void): void => void cleanups.push(fn);
function teardown(): void {
  for (const fn of cleanups.splice(0)) {
    try {
      fn();
    } catch {
      /* a failing teardown must never block the next screen */
    }
  }
}
const setPlaying = (on: boolean): void => void document.body.classList.toggle('playing', on);

function mountShell(): void {
  app.innerHTML = `<main class="main-content" id="content"></main><footer class="site-footer">${ATTRIB}</footer>`;
  content = document.getElementById('content')!;
}

/**
 * A sheet has FOUR ways out — the close button, a tap outside, Escape, and a backdrop that is
 * genuinely reachable — because an overlay with one way out is a game you cannot play. The 350ms
 * arming delay is not a flourish: a sheet opened from a pointerup is otherwise closed instantly by
 * that same tap's trailing click.
 */
function sheet(html: string, onMount?: (el: HTMLElement, close: () => void) => void): void {
  const wrap = document.createElement('div');
  wrap.className = 'overlay';
  wrap.innerHTML = `<div class="sheet" role="dialog" aria-modal="true">${html}</div>`;
  document.body.appendChild(wrap);
  const armedAt = Date.now();
  const close = (): void => {
    wrap.remove();
    window.removeEventListener('keydown', onKey);
  };
  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') close();
  }
  wrap.addEventListener('pointerdown', (e) => {
    if (e.target === wrap && Date.now() - armedAt > 350) close();
  });
  wrap.querySelectorAll('.sheet-close').forEach((b) => b.addEventListener('click', close));
  window.addEventListener('keydown', onKey);
  onMount?.(wrap, close);
}

function toast(msg: string): void {
  const t = document.createElement('div');
  t.className = 'toast';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.classList.add('show'), 10);
  setTimeout(() => t.remove(), 1900);
}

let lastCue = 0;
function ping(cue: string, pitch?: number): void {
  try {
    sfx.play(cue, pitch === undefined ? undefined : { pitch });
  } catch {
    /* audio is best-effort and must never break a round */
  }
}
/** A dozen shots a second would be a buzz rather than a sound; combat cues are rate-limited. */
function pingCombat(cue: string, pitch?: number): void {
  const now = Date.now();
  if (now - lastCue < 55) return;
  lastCue = now;
  ping(cue, pitch);
}

// ── menu ────────────────────────────────────────────────────────────────────────
function showMenu(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = menuHtml({ modeId: myModeChoice, tierId: myTier, best: bestFor(myModeChoice), muted: sfx.muted() });

  content.querySelectorAll<HTMLElement>('.chip[data-mode]').forEach((c) =>
    c.addEventListener('click', () => {
      myModeChoice = modeOf(c.dataset.mode).id;
      store.set('mode', myModeChoice);
      ping('select');
      showMenu();
    }),
  );
  content.querySelectorAll<HTMLElement>('.chip[data-tier]').forEach((c) =>
    c.addEventListener('click', () => {
      myTier = tierOf(c.dataset.tier).id;
      store.set('tier', myTier);
      ping('select');
      showMenu();
    }),
  );
  content.querySelector('#playSolo')!.addEventListener('click', () => startSolo());
  content.querySelector('#playFriends')!.addEventListener('click', () => showRoomEntry());
  content.querySelector('#help')!.addEventListener('click', () => openHelp());
  content.querySelector('#about')!.addEventListener('click', () => sheet(aboutHtml()));
  content.querySelector('#mute')!.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    showMenu();
  });
}

const openHelp = (): void =>
  sheet(helpHtml(modeOf(myModeChoice)), (el, close) => {
    el.querySelector('#tut')?.addEventListener('click', () => {
      close();
      startTutorial();
    });
  });

// ── the game screen ─────────────────────────────────────────────────────────────
interface Screen {
  destroy(): void;
}

let active: Battle | null = null;
let activeSelection = new Set<number>();
let activeStep = -1;
/** Set by the live game screen so the verification pass can ask what a tap would resolve to. */
let activeProbe: ((px: number, py: number) => unknown) | null = null;

function buildGameScreen(b: Battle, o: { onQuit: () => void; onOver: () => void; tutorial: boolean }): Screen {
  const seat = b.mySeat ?? 0;
  content.innerHTML = `
    <section class="screen game">
      <div class="tonn" id="tonn"><i id="tonnFill"></i><span class="tl" id="tonnL"></span><span class="tr" id="tonnR"></span></div>
      <div class="board-host" id="boardHost"><canvas id="board"></canvas></div>
      <p class="coach" id="coach" aria-live="polite"></p>
      <div class="hud">
        <div class="hudline"><span class="ore" id="ore"></span><span class="clock" id="clock"></span>
          <button class="iconbtn" id="pause" title="Pause" aria-label="Pause">❚❚</button></div>
        <div class="builds" id="builds"></div>
        <div class="stances" id="stances">
          <button class="btn st" data-st="0">Hold</button>
          <button class="btn st on" data-st="1">Push</button>
          <button class="btn st" data-st="2">Run</button>
        </div>
      </div>
    </section>`;

  const host = content.querySelector<HTMLElement>('#boardHost')!;
  const canvas = content.querySelector<HTMLCanvasElement>('#board')!;
  const g = canvas.getContext('2d')!;
  const coach = content.querySelector<HTMLElement>('#coach')!;
  const oreEl = content.querySelector<HTMLElement>('#ore')!;
  const clockEl = content.querySelector<HTMLElement>('#clock')!;
  const buildsEl = content.querySelector<HTMLElement>('#builds')!;
  const tonnFill = content.querySelector<HTMLElement>('#tonnFill')!;
  const tonnL = content.querySelector<HTMLElement>('#tonnL')!;
  const tonnR = content.querySelector<HTMLElement>('#tonnR')!;

  let view: View = { tile: 24, ox: 0, oy: 0, w: 0, h: 0, flip: seat === 1 };
  let selection = new Set<number>();
  activeSelection = selection;
  let lasso: { x0: number; y0: number; x1: number; y1: number } | null = null;
  const tracers: number[][] = [];
  let live = false;
  let destroyed = false;
  let paused = false;
  let settleTimer: ReturnType<typeof setTimeout> | null = null;
  let step = o.tutorial ? 0 : -1;
  activeStep = step;
  let stanceChoice = Stance.Push;

  function resize(): void {
    const rect = host.getBoundingClientRect();
    // Guard against a transient 0-size measurement: computing a scale from a 0x0 rect yields NaN
    // world coordinates and silently drops every tap until the next reflow.
    const l = boardSize(rect.width, rect.height, window.innerWidth, window.innerHeight);
    view = { tile: l.tile, ox: 0, oy: 0, w: l.w, h: l.h, flip: seat === 1 };
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    canvas.width = Math.round(l.w * dpr);
    canvas.height = Math.round(l.h * dpr);
    canvas.style.width = `${l.w}px`;
    canvas.style.height = `${l.h}px`;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    draw();

    // MEASURE AGAIN ON THE NEXT TURN OF THE LOOP, ONCE.
    //
    // A ResizeObserver fires while the layout is still settling, so the height it reports after an
    // orientation change (or a browser toolbar collapsing) is the height BEFORE the rest of the
    // column reflowed. Measured: a 1280x800 window laid the board out at a 30px tile and settled at
    // 35 — an eight-thousand-pixel board arriving a sixth smaller than it should be, and staying
    // that way until something else happened to trigger a resize. setTimeout rather than rAF,
    // because a backgrounded tab never fires rAF and would keep the wrong size indefinitely.
    if (settleTimer !== null) clearTimeout(settleTimer);
    settleTimer = setTimeout(() => {
      settleTimer = null;
      if (destroyed) return;
      const again = host.getBoundingClientRect();
      const next = boardSize(again.width, again.height, window.innerWidth, window.innerHeight);
      if (next.tile !== view.tile) resize();
    }, 80);
  }

  function draw(): void {
    if (destroyed) return;
    paint(g, {
      view,
      state: b.state,
      seat,
      selection,
      lasso,
      tracers,
      hint: step >= 0 && STEPS[step] ? STEPS[step].hint(b.state) : -1,
      reducedMotion,
    });
  }

  function renderHud(): void {
    if (destroyed) return;
    const s = b.state;
    const side = s.sides[seat];
    const split = tonnageSplit(s, seat);
    tonnFill.style.width = `${Math.round(split.frac * 100)}%`;
    tonnL.textContent = String(split.mine);
    tonnR.textContent = String(split.theirs);
    oreEl.textContent =
      s.mode.buildBudget > 0
        ? `${side.reserve} ore · ${Math.max(0, s.mode.buildBudget - side.presses)} builds left`
        : `${side.reserve} ore`;
    const secs = b.remaining;
    clockEl.textContent = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
    clockEl.classList.toggle('low', s.tick >= s.mode.rustAt);

    buildsEl.innerHTML = UNITS.map((u, i) => {
      const st = buildState(s, seat, i);
      const q = side.queue.indexOf(i);
      const prog = q === 0 ? Math.round((side.progress / u.build) * 100) : 0;
      return `<button class="btn build${st.can ? '' : ' off'}" data-build="${i}" ${st.can ? '' : 'aria-disabled="true"'}>
        <i style="width:${prog}%"></i><b>${escapeHtml(u.tag)}</b><span>${u.cost}</span>
        ${q > 0 ? '<em>queued</em>' : st.can ? '' : `<em>${escapeHtml(st.why)}</em>`}</button>`;
    }).join('');
    buildsEl.querySelectorAll<HTMLElement>('[data-build]').forEach((el) =>
      el.addEventListener('click', () => {
        const type = Number(el.dataset.build);
        if (step >= 0 && !allowed(step, b.state, { kind: Cmd.Build, unit: -1, arg: type })) {
          ping('deny');
          return;
        }
        if (!buildState(b.state, seat, type).can) {
          ping('deny');
          return;
        }
        b.build(type);
        ping('build');
        renderHud();
      }),
    );

    coach.innerHTML =
      step >= 0 && STEPS[step]
        ? `<b>${step + 1}/${STEPS.length}</b> ${escapeHtml(STEPS[step].text)} <button class="linkbtn" id="skipTut">Skip</button>`
        : b.absent[1 - seat]
          ? waitingHtml(b.names[1 - seat] ?? 'them')
          : '';
    coach.querySelector('#skipTut')?.addEventListener('click', () => finishTutorial());
  }

  function finishTutorial(): void {
    step = -1;
    activeStep = -1;
    store.set('taught', true);
    renderHud();
  }

  function advanceTutorial(): void {
    if (step < 0) return;
    const st = STEPS[step];
    if (!st.done(b.state, selection)) return;
    toast(st.after);
    ping('claim');
    step++;
    activeStep = step;
    if (step >= STEPS.length) finishTutorial();
    else renderHud();
  }

  function issue(orders: Order[]): void {
    if (!orders.length) return;
    if (step >= 0) {
      for (const o of orders) {
        if (!allowed(step, b.state, o)) {
          ping('deny');
          return;
        }
      }
    }
    b.issue(orders);
    ping('order');
  }

  // ── input ─────────────────────────────────────────────────────────────────────
  let downX = 0;
  let downY = 0;
  let downUnit = -1;
  let dragging = false;
  let pressTimer: ReturnType<typeof setTimeout> | null = null;
  let lastTapAt = 0;
  let lastTapUnit = -1;
  let pointer = -1;

  const local = (e: PointerEvent): [number, number] => {
    const r = canvas.getBoundingClientRect();
    // clientX/Y minus the rect, never offsetX/Y: offset scales oddly under DPR and page zoom.
    return [e.clientX - r.left, e.clientY - r.top];
  };

  function onDown(e: PointerEvent): void {
    if (!live || paused || destroyed || b.over || b.mySeat === null) return;
    pointer = e.pointerId;
    canvas.setPointerCapture(e.pointerId);
    const [x, y] = local(e);
    downX = x;
    downY = y;
    dragging = false;
    downUnit = unitAt(b.state, view, x, y, seat);
    if (downUnit >= 0) {
      // Press-and-hold widens the selection from the unit to its cluster. It is the deliberate
      // second step: a tap that selected the whole army would walk your gate Anvils out of the gate.
      pressTimer = setTimeout(() => {
        pressTimer = null;
        if (downUnit < 0 || dragging) return;
        selection = new Set(clusterOf(b.state, downUnit));
        activeSelection = selection;
        ping('select', 0.8);
        draw();
      }, PRESS_MS);
    }
  }

  function onMove(e: PointerEvent): void {
    if (e.pointerId !== pointer || !live || b.mySeat === null) return;
    const [x, y] = local(e);
    if (!dragging && Math.hypot(x - downX, y - downY) > DRAG_SLOP) {
      dragging = true;
      if (pressTimer !== null) {
        clearTimeout(pressTimer);
        pressTimer = null;
      }
    }
    if (dragging) {
      lasso = { x0: downX, y0: downY, x1: x, y1: y };
      draw();
    }
  }

  function onUp(e: PointerEvent): void {
    if (e.pointerId !== pointer) return;
    pointer = -1;
    if (pressTimer !== null) {
      clearTimeout(pressTimer);
      pressTimer = null;
    }
    if (!live || paused || destroyed || b.over || b.mySeat === null) {
      lasso = null;
      return;
    }
    const [x, y] = local(e);
    if (dragging && lasso) {
      const picked = unitsIn(b.state, seat, lasso, (wx, wy) => project(view, wx, wy));
      selection = new Set(picked);
      activeSelection = selection;
      lasso = null;
      dragging = false;
      if (picked.length) ping('select');
      draw();
      renderHud();
      return;
    }
    lasso = null;

    const hit = unitAt(b.state, view, x, y, seat);
    if (hit >= 0) {
      const now = Date.now();
      const isDouble = hit === lastTapUnit && now - lastTapAt < DOUBLE_MS && Math.hypot(x - downX, y - downY) < DOUBLE_PX;
      selection = new Set(isDouble ? sameTypeAs(b.state, hit) : [hit]);
      activeSelection = selection;
      lastTapAt = now;
      lastTapUnit = hit;
      ping('select', isDouble ? 1.2 : 1);
      draw();
      renderHud();
      advanceTutorial();
      return;
    }

    if (selection.size > 0) {
      const tile = unproject(view, x, y);
      const foe = unitAt(b.state, view, x, y, 1 - seat);
      const units = [...selection].filter((u) => b.state.alive[u]);
      if (foe >= 0) {
        issue(units.map((u) => ({ kind: Cmd.Attack, unit: u, arg: foe })));
      } else if (tile >= 0) {
        // Fan the group out around the tap so eleven units do not all shove at one square.
        const tiles = spread(b.state, tile, units);
        issue(units.map((u, i) => ({ kind: Cmd.Move, unit: u, arg: tiles[i] })));
      }
      draw();
      advanceTutorial();
      return;
    }
    void orderFor;
  }

  activeProbe = (px, py) => {
    const r = canvas.getBoundingClientRect();
    const lx = px - r.left;
    const ly = py - r.top;
    return {
      live,
      paused,
      destroyed,
      over: b.over,
      mySeat: b.mySeat,
      viewTile: view.tile,
      local: [Math.round(lx), Math.round(ly)],
      tile: unproject(view, lx, ly),
      unit: unitAt(b.state, view, lx, ly, seat),
      enemy: unitAt(b.state, view, lx, ly, 1 - seat),
    };
  };
  canvas.addEventListener('pointerdown', onDown);
  // move/up on the window, so a drag that leaves the canvas still completes.
  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onUp);

  content.querySelectorAll<HTMLElement>('.st').forEach((el) =>
    el.addEventListener('click', () => {
      stanceChoice = Number(el.dataset.st) as Stance;
      content.querySelectorAll('.st').forEach((x) => x.classList.remove('on'));
      el.classList.add('on');
      const units = [...selection].filter((u) => b.state.alive[u]);
      if (units.length) issue(units.map((u) => ({ kind: Cmd.Stance, unit: u, arg: stanceChoice })));
    }),
  );

  const cd = runCountdown(host, sfx, () => {
    if (destroyed) return;
    live = true;
    renderHud();
    draw();
  });

  // ── clocks ────────────────────────────────────────────────────────────────────
  // Measure the REAL elapsed time rather than trusting the interval. A backgrounded tab clamps
  // timers to about once a second, so feeding a hard-coded 50ms runs the match at a fifth speed.
  let sizeCheck = 0;
  let lastTick = Date.now();
  const clock = setInterval(() => {
    if (destroyed) return;
    const now = Date.now();
    const dt = Math.min(2000, Math.max(0, now - lastTick));
    lastTick = now;
    // A SIZE CHECK ON THE CLOCK, not only on an event. A ResizeObserver can miss an orientation
    // change or a toolbar collapse, or fire before the column has reflowed — and because the canvas
    // is out of flow, a board left sized for the previous viewport hangs off both edges silently.
    // Ten times a second costs nothing and cannot be missed.
    if (++sizeCheck % 10 === 0) {
      const r = host.getBoundingClientRect();
      const want = boardSize(r.width, r.height, window.innerWidth, window.innerHeight);
      if (want.tile !== view.tile) resize();
    }
    if (paused && !b.networked) return;
    if (!live) return;
    b.advance(dt);
    for (let i = tracers.length - 1; i >= 0; i--) {
      tracers[i][4]++;
      if (tracers[i][4] > 6) tracers.splice(i, 1);
    }
    draw();
    renderHud();
    advanceTutorial();
    if (b.over) handoff();
  }, 50);

  const ro = new ResizeObserver(() => resize());
  ro.observe(host);
  window.addEventListener('resize', resize);
  window.addEventListener('orientationchange', resize);

  b.deps.onChange = () => {
    if (!destroyed) renderHud();
  };
  b.deps.onEvents = (events: SimEvent[], s: State) => juice(events, s);
  // THE SCREEN OWNS "AM I STILL THE RIGHT SCREEN", and the callback is only a hint.
  //
  // A two-tab smoke test caught the survivor of a walkover sitting on a live board reading "waiting
  // for…" for ever: the match had genuinely ended — the fault was set, the roster showed the other
  // player absent — and the summary never appeared, because the one callback that navigates did not
  // land. Whatever the reason, a player stranded on a frozen board is the worst outcome this game
  // has, so the clock below checks `b.over` on every tick and the transition cannot be missed. The
  // callback stays as the fast path; `handoff` makes it idempotent.
  let handedOff = false;
  const handoff = (): void => {
    if (handedOff || destroyed) return;
    handedOff = true;
    setTimeout(() => {
      if (!destroyed) o.onOver();
    }, 900);
  };
  b.deps.onEnd = handoff;

  function juice(events: SimEvent[], s: State): void {
    for (const ev of events) {
      if (ev.kind === 'shot') {
        const spec = UNITS[s.type[ev.unit]];
        tracers.push([s.x[ev.unit], s.y[ev.unit], s.x[ev.at], s.y[ev.at], 0, s.seat[ev.unit]]);
        pingCombat(spec.reach > 20 ? 'shot' : 'thud', spec.reach > 20 ? 0.9 : 1.1);
      } else if (ev.kind === 'kill') {
        ping('wreck', 1.4 - ev.amount / 50);
        if (!reducedMotion) {
          host.classList.add('shake');
          setTimeout(() => host.classList.remove('shake'), 220);
        }
      } else if (ev.kind === 'claim') {
        ping(ev.seat === seat ? 'claim' : 'slip');
      } else if (ev.kind === 'build') {
        if (ev.seat === seat) ping('build');
      } else if (ev.kind === 'over') {
        ping(ev.seat === seat ? 'win' : 'lose');
      }
      if (!reducedMotion && 'vibrate' in navigator && ev.kind === 'kill' && ev.seat !== seat) {
        try {
          navigator.vibrate(10);
        } catch {
          /* haptics are best-effort */
        }
      }
    }
  }

  function setPaused(on: boolean): void {
    let ov = host.querySelector<HTMLElement>('.pause-ov');
    if (on) {
      if (ov) return;
      paused = true;
      ov = document.createElement('div');
      ov.className = 'pause-ov';
      ov.innerHTML = `<div class="pause-card" role="dialog" aria-modal="true" aria-label="Paused">
        <h2>Paused</h2>
        <p class="pause-note">${
          b.networked ? 'The match clock keeps running — the other player is still playing.' : 'The board is held until you resume.'
        }</p>
        <button class="btn primary" id="resume">Resume</button>
        <button class="btn" id="phelp">How to play</button>
        <button class="btn ghost" id="pquit">Quit</button></div>`;
      host.appendChild(ov);
      ov.querySelector('#resume')!.addEventListener('click', () => setPaused(false));
      ov.querySelector('#phelp')!.addEventListener('click', () => openHelp());
      ov.querySelector('#pquit')!.addEventListener('click', o.onQuit);
      // Route 2 of the dismissal contract: a tap anywhere off the card closes it. Armed late so the
      // tap that OPENED it cannot immediately close it again.
      setTimeout(() => {
        ov?.addEventListener('pointerdown', (ev) => {
          if (!(ev.target as HTMLElement).closest('.pause-card')) setPaused(false);
        });
      }, 350);
    } else {
      paused = false;
      ov?.remove();
      lastTick = Date.now();
    }
  }
  content.querySelector('#pause')!.addEventListener('click', () => setPaused(!host.querySelector('.pause-ov')));

  function onKey(e: KeyboardEvent): void {
    if (b.mySeat === null) return;
    if (e.key === 'p' || e.key === 'P' || e.key === 'Escape') setPaused(!host.querySelector('.pause-ov'));
    else if (e.key === '1' || e.key === '2' || e.key === '3') {
      const type = Number(e.key) - 1;
      if (buildState(b.state, seat, type).can) {
        b.build(type);
        ping('build');
        renderHud();
      } else ping('deny');
    } else if (e.key === 'a' || e.key === 'A') {
      const all: number[] = [];
      for (let u = 0; u < b.state.nextId; u++) if (b.state.alive[u] && b.state.seat[u] === seat) all.push(u);
      selection = new Set(all);
      activeSelection = selection;
      draw();
    } else if (e.key === 'h' || e.key === 'H') {
      const units = [...selection].filter((u) => b.state.alive[u]);
      if (units.length) issue(units.map((u) => ({ kind: Cmd.Stance, unit: u, arg: Stance.Hold })));
    }
  }
  window.addEventListener('keydown', onKey);

  resize();
  renderHud();

  return {
    destroy() {
      destroyed = true;
      clearInterval(clock);
      if (pressTimer !== null) clearTimeout(pressTimer);
      cd.cancel();
      ro.disconnect();
      if (settleTimer !== null) clearTimeout(settleTimer);
      activeProbe = null;
      canvas.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', resize);
      window.removeEventListener('orientationchange', resize);
    },
  };
}

// ── solo ────────────────────────────────────────────────────────────────────────
let soloScreen: Screen | null = null;

function startSolo(): void {
  teardown();
  setPlaying(true);
  soloRound++;
  const mode = modeOf(myModeChoice);
  const tier = tierOf(myTier);
  const b = new Battle({
    modeId: mode.id,
    seed: newSeed(),
    round: soloRound,
    names: [playerName, tier.name],
    mySeat: 0,
    bots: [null, tier],
    deps: { send: () => {}, onChange: () => {}, onEnd: () => {}, onEvents: () => {} },
  });
  battle = b;
  active = b;
  tally = [0, 0];
  soloScreen = buildGameScreen(b, {
    onQuit: () => showMenu(),
    onOver: () => {
      const mine = b.results()[0];
      if (mine.tonnage > bestFor(mode.id)) store.set(bestKey(mode.id), mine.tonnage);
      showResults(false);
    },
    tutorial: false,
  });
  onCleanup(() => soloScreen?.destroy());
}

function startTutorial(): void {
  teardown();
  setPlaying(true);
  const b = new Battle({
    modeId: 'standing',
    seed: 20260820,
    round: 0,
    names: [playerName, 'Drill'],
    mySeat: 0,
    bots: [null, null],
    deps: { send: () => {}, onChange: () => {}, onEnd: () => {}, onEvents: () => {} },
  });
  // The tutorial's opening position, built with the real spawn() on the real map.
  b.state = tutorialSetup();
  battle = b;
  active = b;
  const screen = buildGameScreen(b, { onQuit: () => showMenu(), onOver: () => showMenu(), tutorial: true });
  onCleanup(() => screen.destroy());
}

// ── multiplayer ─────────────────────────────────────────────────────────────────
let netSend: (m: Wire) => void = () => {};
let chanOff: () => void = () => {};
let lobby: { repaint(): void; destroy(): void } | null = null;
let lobbyMounted = false;
let gameScreen: Screen | null = null;
let beat: ReturnType<typeof setInterval> | null = null;

const repaintLobby = (): void => {
  if (lobbyMounted) lobby?.repaint();
};

const seatOfPeer = (id: string): number => roster.slice(0, MAX_SEATS).findIndex((p) => p.id === id);

function showRoomEntry(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = '<section class="screen"><div id="entry"></div></section>';
  const host = content.querySelector<HTMLElement>('#entry')!;

  const deep = new URL(location.href).searchParams.get('room');
  if (deep) {
    const code = normalizeRoomCode(deep);
    // Honoured ONCE, and cleared on the way in — otherwise a reload (or reopening from a home-screen
    // icon) silently drags the player back into a room they deliberately left.
    clearRoomInUrl();
    if (code.length >= 3) {
      void enterRoom(code, false);
      return;
    }
  }

  const entry = createRoomEntry({
    container: host,
    onSubmit: (code: string, created: boolean) => void enterRoom(normalizeRoomCode(code), created),
    onCancel: showMenu,
    title: 'Play with a friend',
    subtitle: 'Start a room and send the link, or type a code you were given. Two players.',
  });
  onCleanup(() => entry.destroy());
}

async function enterRoom(code: string, created: boolean): Promise<void> {
  teardown();
  setPlaying(false);
  roomCode = code;
  setRoomInUrl(code);
  tally = [0, 0];
  battle = null;
  active = null;

  content.innerHTML =
    '<section class="screen"><div class="lobby-searching"><span class="spinner"></span> Connecting…</div></section>';

  try {
    // ONCE at boot, before ANY mesh: Trystero pre-builds one global connection pool from the first
    // joinRoom's config, so a later call leaves the initiating half of every pair STUN-only.
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fail open — TURN is an upgrade, never a dependency */
  }

  const n = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      // Host transfer is genuinely a no-op here: the battle is derived from (seed, mode, log), which
      // every peer already holds, so there is nothing to take over. It still re-broadcasts, and it
      // NEVER navigates — this fires after a round ends too, and a showMenu() here would eject
      // everyone from the room.
      onHostChange: () => {
        battle?.onHostChanged();
        repaintLobby();
      },
      onPeers: () => repaintLobby(),
      onPeerLeave: (id) => {
        const seat = seatOfPeer(id);
        if (seat >= 0) battle?.seatLeft(seat);
        repaintLobby();
      },
    },
  );
  net = n;

  const r = createRounds<{ mode: string }>({
    net: n,
    playerName,
    minPlayers: 2,
    roundOpts: () => ({ mode: myModeChoice }),
    onRound: (info) => startNetRound(info),
    onChange: (st) => {
      const ho = st.hostOpts as { mode?: string } | null;
      if (ho && typeof ho.mode === 'string') hostOptsMode = modeOf(ho.mode).id;
      repaintLobby();
      refreshResults();
    },
  });
  rounds = r;

  const send = n.channel<Wire>(CHANNELS.orders, (data, peer) => battle?.receive(data, seatOfPeer(peer)));
  netSend = (msg) => send(msg);
  chanOff = () => send.off();

  showLobby();
}

function showLobby(): void {
  const n = net;
  const r = rounds;
  if (!n || !r) return;
  lobbyMounted = true;
  setPlaying(false);

  content.innerHTML = '<section class="screen"><div id="lobby-host"></div></section>';
  const isHost = (): boolean => n.isHost() && n.hostSettled();

  lobby = createLobby({
    container: content.querySelector<HTMLElement>('#lobby-host')!,
    net: n,
    rounds: r,
    roomCode,
    minPlayers: 2,
    maxPlayers: MAX_SEATS,
    onCancel: () => void leaveRoom(),
    share: { title: 'Play Salient with me', text: (c: string) => `Join my Salient room: ${c}` },
    labels: { start: 'Begin the match' },
    modeSlot: () => {
      if (isHost()) {
        return `<div class="lobby-modes" id="lm">${MODE_IDS.map(
          (id) =>
            `<button class="chip mode-btn${id === myModeChoice ? ' on' : ''}" data-mode="${id}" aria-pressed="${
              id === myModeChoice
            }">${escapeHtml(modeOf(id).name)}</button>`,
        ).join('')}<p class="mode-blurb">${escapeHtml(modeOf(myModeChoice).blurb)}</p></div>`;
      }
      // The host's pick is what the room plays, and it arrives GOSSIPED — rendering our own local
      // choice and labelling it the host's is a confident lie, and it has shipped once in this fleet.
      return `<p class="host-pick">${
        n.hostSettled() ? `The host chose <strong>${escapeHtml(modeOf(hostOptsMode).name)}</strong>` : 'Waiting for the host…'
      }</p>`;
    },
    onModeMount: () => {
      if (!isHost()) return;
      content.querySelectorAll<HTMLElement>('#lm .mode-btn').forEach((btn) =>
        btn.addEventListener('click', () => {
          myModeChoice = modeOf(btn.dataset.mode).id;
          store.set('mode', myModeChoice);
          lobby?.repaint();
        }),
      );
    },
  });
  onCleanup(() => {
    lobby?.destroy();
    lobby = null;
  });
}

function startNetRound(info: RoundInfo<{ mode: string }>): void {
  teardown();
  lobbyMounted = false;
  roster = info.players;
  roundNo = info.round;
  const mode = modeOf(info.opts?.mode);
  const seated = roster.slice(0, MAX_SEATS);
  const mySeat = info.seated ? seated.findIndex((p) => p.id === (net?.selfId ?? '')) : -1;

  const names: string[] = [];
  for (let p = 0; p < MAX_SEATS; p++) names.push(seated[p]?.name || SEAT_NAME[p]);
  // Two players CAN arrive with the same handle — the default one is random, and two tabs on one
  // device share the store outright. Identical names make a results screen unreadable.
  if (names[0] === names[1]) for (let p = 0; p < MAX_SEATS; p++) names[p] = `${names[p]} (${SEAT_NAME[p]})`;

  const b = new Battle({
    modeId: mode.id,
    seed: info.seed,
    round: info.round,
    names,
    mySeat: mySeat >= 0 ? mySeat : null,
    bots: [null, null],
    networked: true,
    deps: { send: (msg) => netSend(msg), onChange: () => {}, onEnd: () => {}, onEvents: () => {} },
  });
  battle = b;
  active = b;
  setPlaying(true);

  if (beat !== null) clearInterval(beat);
  // The heartbeat is what makes a lost message a non-event: it re-sends the whole log every 2s, and
  // it is also the liveness signal the walkover waits on.
  beat = setInterval(() => b.broadcast(), 2000);
  onCleanup(() => {
    if (beat !== null) clearInterval(beat);
    beat = null;
  });

  gameScreen = buildGameScreen(b, {
    onQuit: () => {
      // End this peer's round FIRST (phase playing → waiting), or the engine lobby paints nothing
      // for a peer still seated in a live round and the player gets a blank screen.
      rounds?.finish();
      backToLobby();
    },
    onOver: () => showResults(true),
    tutorial: false,
  });
  onCleanup(() => {
    gameScreen?.destroy();
    gameScreen = null;
  });
  b.broadcast();
}

// ── results ─────────────────────────────────────────────────────────────────────
let resultsMounted = false;
let multi = false;

function showResults(isMulti: boolean): void {
  teardown();
  setPlaying(false);
  multi = isMulti;
  resultsMounted = true;
  const b = battle;
  if (!b) {
    showMenu();
    return;
  }
  for (const r of b.results()) if (r.won) tally[r.seat]++;
  if (isMulti) rounds?.finish();

  content.innerHTML = `<section class="screen results-screen" id="res"></section><div class="menu-actions" id="ract"></div>`;
  renderResults();
  onCleanup(() => {
    resultsMounted = false;
  });
}

function renderResults(): void {
  const b = battle;
  if (!b || !resultsMounted) return;
  const box = content.querySelector<HTMLElement>('#res');
  const act = content.querySelector<HTMLElement>('#ract');
  if (!box || !act) return;

  const rows = b.results();
  box.innerHTML = resultsHtml({
    rows,
    mode: b.mode,
    round: multi ? roundNo : soloRound,
    ending: b.state.ending,
    fault: b.fault,
    tally,
    worldBurnt: rows.reduce((a, r) => a + r.burnt, 0),
  });

  act.innerHTML = multi
    ? `<button class="btn primary" id="again">Play again</button>
       <p class="waiting-note" id="waiting" aria-live="polite"></p>
       <button class="btn" id="lobby">Back to lobby</button>
       <button class="btn ghost" id="menu">Main menu</button>
       <button class="btn ghost results-feedback" id="feedback">Report a problem</button>`
    : `<button class="btn primary" id="again">Play again</button>
       <button class="btn" id="share">Share</button>
       <button class="btn ghost" id="menu">Main menu</button>
       <button class="btn ghost results-feedback" id="feedback">Report a problem</button>`;

  act.querySelector('#feedback')!.addEventListener('click', (e) =>
    window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }),
  );
  act.querySelector('#menu')!.addEventListener('click', () => (multi ? void leaveRoom() : showMenu()));
  if (multi) {
    const again = act.querySelector<HTMLButtonElement>('#again')!;
    again.addEventListener('click', () => {
      rounds?.vote();
      again.disabled = true;
      again.textContent = 'Waiting for the other player…';
      refreshResults();
    });
    act.querySelector('#lobby')!.addEventListener('click', () => backToLobby());
    refreshResults();
  } else {
    act.querySelector('#again')!.addEventListener('click', () => startSolo());
    act.querySelector('#share')!.addEventListener('click', () => void share());
  }
}

function refreshResults(): void {
  if (!resultsMounted || !multi) return;
  const waiting = content.querySelector<HTMLElement>('#waiting');
  if (!waiting) return;
  const r = rounds?.state();
  if (r && r.startsInMs !== null) {
    waiting.textContent = `Next round in ${Math.ceil(r.startsInMs / 1000)}s — ${r.votes.length}/${Math.max(r.present.length, 1)} ready`;
  } else if (r && r.votes.length) {
    waiting.textContent = `${r.votes.length}/${Math.max(r.present.length, 1)} ready for another round`;
  } else {
    waiting.textContent = '';
  }
}

setInterval(() => refreshResults(), 400);

async function share(): Promise<void> {
  const b = battle;
  if (!b || b.mySeat === null) return;
  const mine = b.results()[b.mySeat];
  const text = `Salient · ${b.mode.name} — ${mine.tonnage} tonnage, ${mine.kills} killed, ${mine.salvaged} ore scavenged off the dead\nhttps://salient.benrichardson.dev`;
  try {
    if (navigator.share) {
      await navigator.share({ text });
      return;
    }
  } catch {
    /* cancelled — fall through to copy */
  }
  try {
    await navigator.clipboard.writeText(text);
    toast('Copied to clipboard');
  } catch {
    toast('Could not copy');
  }
}

function backToLobby(): void {
  teardown();
  battle = null;
  active = null;
  if (net && rounds) showLobby();
  else showMenu();
}

async function leaveRoom(): Promise<void> {
  teardown();
  clearRoomInUrl();
  lobbyMounted = false;
  battle = null;
  active = null;
  const n = net;
  const r = rounds;
  net = null;
  rounds = null;
  netSend = () => {};
  try {
    chanOff();
  } catch {
    /* the channel may already be gone */
  }
  chanOff = () => {};
  r?.destroy();
  if (n) {
    try {
      await n.leave();
    } catch {
      /* leaving is best-effort */
    }
  }
  showMenu();
}

// ── boot ────────────────────────────────────────────────────────────────────────
function boot(): void {
  hardenViewport();
  mountShell();
  const room = new URL(location.href).searchParams.get('room');
  if (room && normalizeRoomCode(room).length >= 3) showRoomEntry();
  else if (!store.get<boolean>('taught', false)) startTutorial();
  else showMenu();
  window.addEventListener('beforeunload', () => {
    void net?.leave();
  });
}

// Exposed so the browser-verification pass can drive the REAL paths synchronously — rAF is throttled
// in a hidden tab and every phone check runs through this. Never read by gameplay.
(window as unknown as { __salient?: unknown }).__salient = {
  state(): unknown {
    const b = active;
    if (!b) return null;
    const s = b.state;
    const units: unknown[] = [];
    for (let e = 0; e < s.nextId; e++) {
      if (!s.alive[e]) continue;
      units.push({ e, seat: s.seat[e], type: s.type[e], tile: tileOf(s, e), hp: s.hp[e], armour: s.armour[e] });
    }
    return {
      mode: s.modeId,
      tick: s.tick,
      over: s.over,
      winner: s.winner,
      ending: s.ending,
      fault: b.fault,
      mySeat: b.mySeat,
      reserve: [s.sides[0].reserve, s.sides[1].reserve],
      forge: [s.sides[0].forgeHp, s.sides[1].forgeHp],
      tonnage: [tonnage(s, 0), tonnage(s, 1)],
      seams: s.seams.map((x) => ({ tile: x.tile, owner: x.owner, ore: x.ore })),
      wrecks: s.wrecks.map((w) => ({ tile: w.tile, ore: w.ore })),
      units,
      selection: [...activeSelection],
      step: activeStep,
      log: b.log.length / 4,
    };
  },
  /** Where a tile is on screen, so a synthetic tap can be aimed without guessing. */
  rect(tile: number): unknown {
    const canvas = document.querySelector<HTMLCanvasElement>('#board');
    const b = active;
    if (!canvas || !b) return null;
    const r = canvas.getBoundingClientRect();
    // The tile size comes from the canvas ITSELF, never from re-running layout() on the canvas's own
    // size: layout subtracts a margin from the width it is given, so feeding it the board's width
    // returns a slightly smaller tile and every synthetic tap drifts further from its target the
    // closer it is to the far corner. The first three tutorial steps passed and the fourth did not.
    const px = r.width / COLS;
    const v: View = { tile: px, ox: 0, oy: 0, w: r.width, h: r.height, flip: (b.mySeat ?? 0) === 1 };
    const [x, y, w, h] = tileRect(v, tile);
    return { x: r.left + x, y: r.top + y, w, h, cx: r.left + x + w / 2, cy: r.top + y + h / 2 };
  },
  probe(px: number, py: number): unknown {
    return activeProbe ? activeProbe(px, py) : null;
  },
  modes: MODE_IDS,
  tiers: TIER_IDS,
  steps: STEPS.length,
  /** Forget the tutorial flag, so a fresh-device first run can be verified without a new profile. */
  forget(): void {
    store.set('taught', false);
  },
};

boot();
